/**
 * Unified field selection — the single source of truth for "which target
 * field is the operator looking at". Canvas, mapping-table view, and the
 * review pane all read/write through this hook so the right-side
 * Inspector re-scopes consistently regardless of which surface
 * triggered the selection.
 *
 * Keyed on `(target_table, target_field, hierarchy_path?)`. The
 * `hierarchy_path` is required to disambiguate same-named fields at
 * different structural levels in nested targets (XML/JSON/ODM/
 * hierarchical-assembly). Flat tabular targets omit it.
 *
 * A `scope: 'table'` variant lets table-group / batch-level review
 * items (skipped tables, batch-level rule violations, transformation
 * hints) target a whole table without a field.
 */
import { useCallback, useState } from 'react';
import type { CanvasSelection } from '../types';

export type FieldSelection =
  | {
      scope: 'field';
      target_table: string;
      target_field: string;
      hierarchy_path?: Record<string, string>;
      source: 'canvas' | 'table' | 'review-pane';
    }
  | {
      scope: 'table';
      target_table: string;
      source: 'canvas' | 'table' | 'review-pane';
    }
  | null;

/**
 * Stable canonical key. Used for de-dup in `useReviewItems`, for
 * scroll targeting via `data-row-id`, and for review-pane active-row
 * highlighting.
 *
 * Format:
 *   - field-scope: `F::table::field::sortedHierarchyPath`
 *   - table-scope: `T::table`
 *   - null:        ``
 */
export function fieldKey(s: FieldSelection): string {
  if (!s) return '';
  if (s.scope === 'table') return `T::${s.target_table}`;
  const hp = s.hierarchy_path
    ? Object.entries(s.hierarchy_path)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([k, v]) => `${k}=${v}`)
        .join('/')
    : '';
  return `F::${s.target_table}::${s.target_field}::${hp}`;
}

/**
 * Build a key for an arbitrary `(table, field?)` pair without
 * constructing a full `FieldSelection` — used by per-row chip lookups
 * and review-item id construction.
 */
export function makeFieldKey(
  target_table: string,
  target_field?: string,
  hierarchy_path?: Record<string, string>,
): string {
  if (!target_field) return `T::${target_table}`;
  const hp = hierarchy_path
    ? Object.entries(hierarchy_path)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([k, v]) => `${k}=${v}`)
        .join('/')
    : '';
  return `F::${target_table}::${target_field}::${hp}`;
}

/**
 * Adapt a canvas selection (node/edge) to a `FieldSelection`. Returns
 * `null` when the selection doesn't address a target field/table —
 * e.g. source-side nodes, transformation nodes (those keep using
 * canvas-only selection state for their own inspector views).
 */
export function canvasSelectionToField(sel: CanvasSelection | null): FieldSelection {
  if (!sel) return null;

  if (sel.kind === 'node') {
    const data = sel.node.data;
    if (!data) return null;
    const source = 'canvas' as const;

    if (data.kind === 'targetField') {
      const hp = (data.mapping?.hierarchy_path ?? undefined) as
        | Record<string, string>
        | undefined;
      return {
        scope: 'field',
        target_table: data.table,
        target_field: data.field,
        hierarchy_path: hp,
        source,
      };
    }

    if (data.kind === 'tableGroup' && data.groupRole === 'target') {
      return { scope: 'table', target_table: data.table, source };
    }

    return null;
  }

  // Edge selection — addresses the target field on the receiving end.
  if (sel.kind === 'edge') {
    const m = sel.edge.data?.mappingId;
    if (!m) return null;
    // We don't have the mapping in scope here; the editor resolves
    // edge → mapping via the mapping list. Return null and let the
    // editor's adapter call setFieldSelection directly with the
    // resolved address.
    return null;
  }

  return null;
}

export interface UseFieldSelectionResult {
  selection: FieldSelection;
  setSelection: (next: FieldSelection) => void;
  clear: () => void;
  /** Convenience: set field-scope selection. */
  selectField: (
    target_table: string,
    target_field: string,
    source: 'canvas' | 'table' | 'review-pane',
    hierarchy_path?: Record<string, string>,
  ) => void;
  /** Convenience: set table-scope selection. */
  selectTable: (
    target_table: string,
    source: 'canvas' | 'table' | 'review-pane',
  ) => void;
  /** Stable key of current selection (empty string for null). */
  key: string;
  /** Test whether an arbitrary `(table, field?)` matches the current selection. */
  matches: (
    target_table: string,
    target_field?: string,
    hierarchy_path?: Record<string, string>,
  ) => boolean;
}

export function useFieldSelection(): UseFieldSelectionResult {
  const [selection, setSelectionInner] = useState<FieldSelection>(null);

  const setSelection = useCallback((next: FieldSelection) => {
    setSelectionInner((prev) => {
      if (fieldKey(prev) === fieldKey(next)) return prev;
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelectionInner(null);
  }, []);

  const selectField = useCallback(
    (
      target_table: string,
      target_field: string,
      source: 'canvas' | 'table' | 'review-pane',
      hierarchy_path?: Record<string, string>,
    ) => {
      setSelection({
        scope: 'field',
        target_table,
        target_field,
        hierarchy_path,
        source,
      });
    },
    [setSelection],
  );

  const selectTable = useCallback(
    (target_table: string, source: 'canvas' | 'table' | 'review-pane') => {
      setSelection({ scope: 'table', target_table, source });
    },
    [setSelection],
  );

  const matches = useCallback(
    (
      target_table: string,
      target_field?: string,
      hierarchy_path?: Record<string, string>,
    ): boolean => {
      if (!selection) return false;
      if (target_field === undefined) {
        return selection.scope === 'table' && selection.target_table === target_table;
      }
      if (selection.scope !== 'field') return false;
      if (selection.target_table !== target_table) return false;
      if (selection.target_field !== target_field) return false;
      const a = selection.hierarchy_path ?? {};
      const b = hierarchy_path ?? {};
      const ak = Object.keys(a);
      const bk = Object.keys(b);
      if (ak.length !== bk.length) return false;
      return ak.every((k) => a[k] === b[k]);
    },
    [selection],
  );

  return {
    selection,
    setSelection,
    clear,
    selectField,
    selectTable,
    key: fieldKey(selection),
    matches,
  };
}
